/**
 * Liquidity Queue Hooks
 * TanStack Query hooks for transactions queued for company liquidity
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
    cancelQueuedTransaction,
    getQueuedTransactions,
    restartQueuedTransaction,
} from '@/services/liquidity-queue.service'
import type { QueuedTransactionsResponse } from '@/types/liquidity-queue'

const QUEUE_KEY = 'liquidity-queue'

export const useQueuedTransactions = (page = 1, limit = 20) => {
    return useQuery<QueuedTransactionsResponse>({
        queryKey: [QUEUE_KEY, page, limit],
        queryFn: () => getQueuedTransactions(page, limit),
        staleTime: 1000 * 15,
    })
}

/** Both actions change the queue and the transaction list. */
const useQueueMutation = (
    fn: (id: string) => ReturnType<typeof cancelQueuedTransaction>
) => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: fn,
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: [QUEUE_KEY] })
            queryClient.invalidateQueries({ queryKey: ['transactions'] })
        },
    })
}

export const useRestartQueuedTransaction = () =>
    useQueueMutation(restartQueuedTransaction)

export const useCancelQueuedTransaction = () =>
    useQueueMutation(cancelQueuedTransaction)
